import { useMemo, useState } from 'react';
import type { SessionResource, SessionResourceLibrary } from '@lp/shared';
import { ChevronRight, Download, ExternalLink, Folder, FolderOpen } from 'lucide-react';
import { formatBytes } from '../../lib/format.js';
import { childrenOf, downloadResource, itemsWithin, pathTo } from '../../lib/resourceTree.js';
import { useToast } from '../Toast.js';
import { Button, Card, EmptyState, SectionHeading } from '../ui.js';
import { FileTypeIcon } from '../FileTypeIcon.js';

/**
 * Reference documents: the trainer's library for this session, in folders.
 *
 * Everything here is download-only. Office files, PDFs and datasets are not
 * opened in the page — a learner gets the file type, the size and a download
 * button, and their own software opens it.
 */
export function ResourcesSection({ library }: { library: SessionResourceLibrary }) {
  const [folderId, setFolderId] = useState<string | null>(null);
  // A folder that vanished on refetch (the author deleted it) drops back to the top.
  const current = folderId && library.folders.some((f) => f.id === folderId) ? folderId : null;
  const { folders, resources } = useMemo(() => childrenOf(library, current), [library, current]);
  const trail = pathTo(library, current);

  return (
    <section aria-labelledby="session-resources">
      <SectionHeading id="session-resources">Reference documents</SectionHeading>

      <nav aria-label="Folder" className="mb-3">
        <ol className="flex flex-wrap items-center gap-1 text-sm">
          <li>
            <button
              type="button"
              onClick={() => setFolderId(null)}
              aria-current={current === null ? 'page' : undefined}
              className="rounded-control px-1.5 py-0.5 text-ink-muted hover:text-ink hover:underline aria-[current=page]:font-medium aria-[current=page]:text-ink"
            >
              All documents
            </button>
          </li>
          {trail.map((f) => (
            <li key={f.id} className="flex items-center gap-1">
              <ChevronRight size={14} className="text-ink-subtle" aria-hidden />
              <button
                type="button"
                onClick={() => setFolderId(f.id)}
                aria-current={current === f.id ? 'page' : undefined}
                className="rounded-control px-1.5 py-0.5 text-ink-muted hover:text-ink hover:underline aria-[current=page]:font-medium aria-[current=page]:text-ink"
              >
                {f.name}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {!folders.length && !resources.length ? (
        <EmptyState
          icon={<FolderOpen size={28} aria-hidden />}
          title={current ? 'This folder is empty' : 'No reference documents yet'}
          description={
            current
              ? 'Nothing has been put in this folder yet.'
              : 'The trainer has not shared any reference documents for this session.'
          }
        />
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {folders.map((f) => {
              const count = itemsWithin(library, f.id);
              return (
                <li key={f.id}>
                  <button
                    type="button"
                    onClick={() => setFolderId(f.id)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-2/60"
                  >
                    <span className="flex w-12 shrink-0 justify-center">
                      <span className="flex h-9 w-9 items-center justify-center rounded-control border border-line bg-surface-2 text-ink-muted">
                        <Folder size={18} aria-hidden />
                      </span>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink">{f.name}</span>
                      <span className="block text-xs text-ink-muted">
                        {count === 1 ? '1 item' : `${count} items`}
                      </span>
                    </span>
                    <ChevronRight size={16} className="text-ink-subtle" aria-hidden />
                  </button>
                </li>
              );
            })}
            {resources.map((r) => (
              <ResourceRow key={r.id} resource={r} />
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

function ResourceRow({ resource }: { resource: SessionResource }) {
  const { toastError } = useToast();
  const [busy, setBusy] = useState(false);
  const isLink = resource.kind === 'link';

  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <FileTypeIcon type={resource.fileType} fileName={resource.fileName} link={isLink} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink">{resource.title}</p>
        <p className="truncate text-xs text-ink-muted">
          {isLink
            ? resource.url
            : [resource.fileName !== resource.title ? resource.fileName : null, formatBytes(resource.fileSize)]
                .filter(Boolean)
                .join(' · ')}
        </p>
        {resource.description ? (
          <p className="mt-1 text-sm leading-relaxed text-ink-muted">{resource.description}</p>
        ) : null}
      </div>
      {isLink && resource.url ? (
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-2"
        >
          <ExternalLink size={14} aria-hidden />
          Open
          <span className="sr-only"> {resource.title} in a new tab</span>
        </a>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          className="shrink-0"
          loading={busy}
          icon={<Download size={14} aria-hidden />}
          aria-label={`Download ${resource.title}`}
          onClick={() => {
            setBusy(true);
            downloadResource(resource.id)
              .catch((err: unknown) => toastError(err, 'The file could not be downloaded.'))
              .finally(() => setBusy(false));
          }}
        >
          Download
        </Button>
      )}
    </li>
  );
}
